import { serverApi } from '../index';

// types
import { PaginatedReq, PaginatedRes } from 'types/pagination';
import { Property, PropertyBuildingsCount, PropertyUnitsCount } from 'types/property';
import { Sensor, SensorByEntityFilter } from 'types/sensor';
import { KeySetPaginationReq, KeySetPaginationRes } from 'types/key-set-pagination';
import { Parsed } from 'types/submission';
import { AlertsOverview, Notification, NotificationItem, OpenLeak } from 'types/notification';
import { Building } from 'types/building';
import { TimeIntervalEnum } from 'types/time-interval.enum';
import { Invoice } from 'types/savings';
import { CityBill } from 'types/city-bill';
import { CompanyTopGallonsUnits } from 'types/company';
import { WaterVolumeType } from 'types/location';

export const propertyApi = serverApi.injectEndpoints({
  endpoints: (builder) => ({
    getProperties: builder.query<PaginatedRes<Property>, PaginatedReq | void>({
      query: (arg) => {
        const { filter, ...others } = arg || {};
        return {
          url: `/properties`,
          params: {
            ...others,
            ...filter
          }
        };
      },
      providesTags: (result) => (result ? [...result.data.map(({ id }) => ({ type: 'Property' as const, id })), 'Property'] : ['Property'])
    }),
    getProperty: builder.query<Property, number>({
      query: (id) => `/properties/${id}`,
      providesTags: (_, __, id) => [{ type: 'Property', id }]
    }),
    updatePropertyCreateAlertCostThreshold: builder.mutation<Property, { property_id: number; cost?: number | null }>({
      query: ({ property_id, ...body }) => ({ url: `/properties/${property_id}/update-create-alert-cost-threshold`, method: 'put', body }),
      invalidatesTags: (_, __, { property_id }) => [{ type: 'Property', id: property_id }]
    }),
    getPorpertyBuildingsCount: builder.query<PropertyBuildingsCount, number>({
      query: (id) => `/properties/${id}/buildings-count`
    }),
    removeProperty: builder.mutation<void, number>({
      query: (id) => ({ url: `/properties/${id}`, method: 'DELETE' }),
      invalidatesTags: (_, __, id) => [{ type: 'Property', id }]
    }),
    createProperty: builder.mutation<void, Omit<Property, 'id' | 'created' | 'modified'>>({
      query: (body) => ({ url: '/properties', method: 'POST', body }),
      invalidatesTags: ['Property']
    }),
    updateProperty: builder.mutation<void, Omit<Property, 'created' | 'modified' | 'company_id'>>({
      query: (data) => {
        const { id, ...body } = data;
        return { url: `/properties/${id}`, method: 'PUT', body };
      },
      invalidatesTags: (_, __, { id }) => [{ type: 'Property', id }]
    }),
    getSensorsByProperty: builder.query<PaginatedRes<Sensor>, SensorByEntityFilter & PaginatedReq>({
      query: ({ id, ...arg }) => {
        const { filter, ...others } = arg;
        return {
          url: `/properties/${id}/sensors`,
          params: {
            ...others,
            ...filter
          }
        };
      },
      providesTags: (result) => (result ? [...result.data.map(({ id }) => ({ type: 'Sensor' as const, id })), 'Sensor'] : ['Sensor'])
    }),
    getSubmissionsByProperty: builder.query<KeySetPaginationRes<Parsed>, { id: number } & KeySetPaginationReq>({
      query: ({ id, ...arg }) => {
        return { url: `/properties/${id}/submissions`, params: arg || undefined };
      },
      providesTags: (result) =>
        result ? [...result.docs.map(({ _id }) => ({ type: 'ParsedSubmission' as const, _id })), 'ParsedSubmission'] : ['ParsedSubmission']
    }),
    getSubmissionsByPropertyCount: builder.query<{ total_count: number }, { filter?: KeySetPaginationReq['filter']; id: number }>({
      query: ({ id, ...arg }) => {
        return { url: `/properties/${id}/submissions/count`, params: arg || undefined };
      }
    }),
    getAlertsByProperty: builder.query<KeySetPaginationRes<Notification>, { id: number } & KeySetPaginationReq>({
      query: ({ id, ...arg }) => {
        return { url: `/properties/${id}/alerts`, params: arg || undefined };
      },
      providesTags: (result) =>
        result ? [...result.docs.map(({ _id }) => ({ type: 'Notification' as const, _id })), 'Notification'] : ['Notification']
    }),
    getAlertsByPropertyCount: builder.query<{ total_count: number }, { id: number; filter: KeySetPaginationReq['filter'] }>({
      query: ({ id, ...arg }) => {
        return { url: `/properties/${id}/alerts/count`, params: arg || undefined };
      },
      providesTags: ['Notification']
    }),
    getPropertyOpenAlertsCount: builder.query<{ company_id: number; open_alerts_count: number }, number>({
      query: (id) => `/properties/${id}/open-alerts-count`,
      providesTags: ['Notification']
    }),
    getPropertyStatistics: builder.query<Record<string, any>, number>({ query: (id) => `/properties/${id}/statistics` }),
    getPropertyGallonsByAppliance: builder.query<{ appliance: string; gallons: number }[], { id: number; from: number; to: number }>({
      query: ({ id, from, to }) => `/properties/${id}/appliance-gallons?from=${from}&to=${to}`
    }),
    getPropertyGallons: builder.query<
      { date: string; items: [{ name: string; gallons: number }] }[],
      { id: number; from: number; to: number; ptpd: number; timeInterval: TimeIntervalEnum }
    >({
      query: ({ id, ...args }) => ({ url: `/properties/${id}/gallons`, params: args })
    }),
    getPropertyBuildingsNames: builder.query<Building[], { id: number; sort?: 'ASC' | 'DESC'; excludeFlume?: boolean }>({
      query: ({ id, ...params }) => ({ url: `/properties/${id}/buildings-names`, params }),
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: 'Building' as const, id })), 'Building'] : ['Building'])
    }),
    getPropertyBuildings: builder.query<PaginatedRes<Building>, { id: number } & PaginatedReq>({
      query: ({ id, ...arg }) => {
        const { filter, ...others } = arg || {};
        return {
          url: `/properties/${id}/buildings`,
          params: {
            ...others,
            ...filter
          }
        };
      },
      providesTags: (result) => (result ? [...result.data.map(({ id }) => ({ type: 'Building' as const, id })), 'Building'] : ['Building'])
    }),
    getSubmissionsGallonsCountByProperty: builder.query<{ gallons_count: number }, { id: number } & KeySetPaginationReq>({
      query: ({ id, ...arg }) => {
        return { url: `/properties/${id}/submissions-gallons-count`, params: arg || undefined };
      },
      providesTags: ['ParsedSubmission']
    }),
    getTopProperties: builder.query<Property[], string>({
      query: (name) => ({ url: `/properties/search-top/${encodeURIComponent(name)}/5` }),
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: 'Property' as const, id })), 'Property'] : ['Property'])
    }),
    getPropertyUnitsCount: builder.query<PropertyUnitsCount, number>({
      query: (id) => `/properties/${id}/units-count`
    }),
    getPropertyTotalUnitCount: builder.query<PropertyUnitsCount, number>({
      query: (id) => `/properties/${id}/total-unit-count`
    }),
    getPropertyGallonsReport: builder.query<any[], { id: number; from: number; to: number }>({
      query: ({ id, ...args }) => ({ url: `/properties/${id}/gallons/report`, params: args })
    }),
    getPropertyShippingDate: builder.query<string, number>({
      query: (id) => ({ url: `/properties/${id}/shipping-date` })
    }),
    changeCompany: builder.mutation<void, { property_id: number; company_id: number; transfer: number }>({
      query: ({ property_id, company_id, transfer }) => {
        return { url: `/properties/${property_id}/change-company/${company_id}/${transfer}`, method: 'PUT' };
      },
      invalidatesTags: (_, __, { property_id }) => [{ type: 'Property', property_id }]
    }),
    getPropertyPtpdThreshold: builder.query<number, { id: number }>({
      query: ({ id }) => `/properties/${id}/ptpd-threshold`
    }),
    getPropertyWaterVolumeType: builder.query<WaterVolumeType, number>({
      query: (id) => `/properties/get-water-volume-type/${id}`
    }),
    getTopBuildingsByGallonsUsage: builder.query<{ gallons: number; id: number }[], { id: number; daysAgo?: number }>({
      query: ({ id, ...params }) => {
        return { url: `/properties/${id}/top-gallons-buildings`, params };
      },
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: 'Building' as const, id })), 'Building'] : ['Building'])
    }),
    getPropertyInvoices: builder.query<PaginatedRes<Invoice>, { id: number } & PaginatedReq>({
      query: ({ id, filter, ...others }) => {
        return { url: `/properties/${id}/invoices`, params: { ...filter, ...others } };
      }
    }),
    getPropertyCityBills: builder.query<PaginatedRes<CityBill>, { id: number } & PaginatedReq>({
      query: ({ id, ...arg }) => {
        const { filter, ...others } = arg || {};
        return {
          url: `/properties/${id}/city-bills`,
          params: {
            ...others,
            ...filter
          }
        };
      },
      providesTags: (result) => (result ? [...result.data.map(({ id }) => ({ type: 'CityBill' as const, id })), 'CityBill'] : ['CityBill'])
    }),
    getPropertyAlertsOverview: builder.query<AlertsOverview, { id: number }>({
      query: ({ id }) => `/properties/${id}/alerts-overview`,
      providesTags: ['Notification']
    }),
    getPropertyTopBuildings: builder.query<Building[], { property_id: number; name: string }>({
      query: ({ property_id, name }) => ({
        url: `/properties/${property_id}/search-top-buildings/${encodeURIComponent(name)}/5`
      }),
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: 'Building' as const, id })), 'Building'] : ['Building'])
    }),
    getPropertyTopGallonsUnits: builder.query<CompanyTopGallonsUnits[], { id: number }>({
      query: ({ id }) => ({ url: `/properties/${id}/top-gallons-units` })
    }),
    getPropertyReportOverview: builder.query<NotificationItem[], { id: number; report: number }>({
      query: ({ id, report }) => `/properties/${id}/report-overview/${report}`,
      providesTags: ['Notification']
    }),
    getPropertyFormattedOpenLeaks: builder.query<OpenLeak[], { id: number }>({
      query: ({ id }) => `/properties/${id}/open-leaks`,
      providesTags: ['Notification']
    }),
    getPropertyChartAlerts: builder.query<
      { date: string; items: [{ name: string; alerts: number }] }[],
      {
        id: number;
        from: number;
        to: number;
        buildingIds?: number[];
        resolved?: boolean;
      }
    >({
      query: ({ id, ...args }) => ({ url: `/properties/${id}/dashboard-chart-alerts`, params: args })
    })
  }),
  overrideExisting: false
});

export const {
  useGetPropertyQuery,
  useGetPorpertyBuildingsCountQuery,
  useRemovePropertyMutation,
  useCreatePropertyMutation,
  useUpdatePropertyMutation,
  useUpdatePropertyCreateAlertCostThresholdMutation,
  useLazyGetPropertyQuery,
  useGetSensorsByPropertyQuery,
  useGetSubmissionsByPropertyQuery,
  useGetAlertsByPropertyQuery,
  useGetPropertyOpenAlertsCountQuery,
  useGetPropertyGallonsByApplianceQuery,
  useGetPropertyBuildingsQuery,
  useLazyGetPropertyBuildingsNamesQuery,
  useLazyGetPropertyOpenAlertsCountQuery,
  useGetSubmissionsGallonsCountByPropertyQuery,
  useGetSubmissionsByPropertyCountQuery,
  useLazyGetTopPropertiesQuery,
  useGetPropertyUnitsCountQuery,
  useLazyGetPropertyGallonsReportQuery,
  useLazyGetPropertyGallonsQuery,
  useGetAlertsByPropertyCountQuery,
  useGetPropertiesQuery,
  useGetPropertyShippingDateQuery,
  useGetPropertyBuildingsNamesQuery,
  useChangeCompanyMutation,
  useGetPropertyPtpdThresholdQuery,
  useGetTopBuildingsByGallonsUsageQuery,
  useLazyGetTopBuildingsByGallonsUsageQuery,
  useGetPropertyInvoicesQuery,
  useGetPropertyCityBillsQuery,
  useGetPropertyAlertsOverviewQuery,
  useLazyGetPropertyTopBuildingsQuery,
  useGetPropertyTopGallonsUnitsQuery,
  useGetPropertyReportOverviewQuery,
  useGetPropertyFormattedOpenLeaksQuery,
  useLazyGetPropertyChartAlertsQuery,
  useLazyGetPropertiesQuery,
  useGetPropertyWaterVolumeTypeQuery,
  useGetPropertyTotalUnitCountQuery
} = propertyApi;
